import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";

// Minimal local Typewriter component to avoid external dependency
const Typewriter = ({
    words = [],
    loop = true,
    typeSpeed = 75,
    deleteSpeed = 50,
    cursor = "_",
}) => {
    const [wordIndex, setWordIndex] = useState(0);
    const [display, setDisplay] = useState("");
    const [deleting, setDeleting] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        const current = words[wordIndex] || "";
        const isDone = !loop && wordIndex >= words.length;
        if (isDone) return;

        const tick = () => {
            const nextLen = deleting ? display.length - 1 : display.length + 1;
            const nextText = current.slice(0, Math.max(0, Math.min(current.length, nextLen)));
            setDisplay(nextText);

            if (!deleting && nextText === current) {
                setTimeout(() => setDeleting(true), 700);
            } else if (deleting && nextText === "") {
                setDeleting(false);
                setWordIndex((i) => (i + 1) % (words.length || 1));
            }
        };

        const delay = deleting ? deleteSpeed : typeSpeed;
        timerRef.current = setTimeout(tick, delay);
        return () => timerRef.current && clearTimeout(timerRef.current);
    }, [display, deleting, wordIndex, words, loop, typeSpeed, deleteSpeed]);

    useEffect(() => {
        // Reset when word index changes
        setDisplay("");
        setDeleting(false);
    }, [wordIndex]);

    return (
        <span>
            {display}
            <span className="ml-1 opacity-80">{cursor}</span>
        </span>
    );
};

const Desktop20 = () => {

    const navigate = useNavigate();

    const [openFeature, setOpenFeature] = useState(null);
    const [openFAQ, setOpenFAQ] = useState(null);



    const features = [
        {
            id: 1,
            title: "AI Resume Scoring",
            desc: "Optimize your resume with AI-driven scoring and feedback. Generate tailored bullet points, quantify impact, and export for ATS-friendly versions.",
            img: require("../img/cat-resume.png"),
            color: "bg-purple-200",
            font: "font-michroma",
        },
        {
            id: 2,
            title: "AI Job Matching",
            desc: "Receive personalized job recommendations with fit scores and explanations so you understand why a job suits you.",
            img: require("../img/cat-jobmatching.png"),
            color: "bg-yellow-200",
        },
        {
            id: 3,
            title: "Career Guidance",
            desc: "Get personalized roadmaps to your dream career — job stages, suggested courses, and weekly milestones aligned with your goals.",
            img: require("../img/cat-career.png"),
            color: "bg-purple-200",
        },
        {
            id: 4,
            title: "Interview Simulation",
            desc: "Practice with a realistic AI interviewer for holistic feedback — communication, domain knowledge, and behavioral answers are scored.",
            img: require("../img/cat-laptop.png"),
            color: "bg-yellow-200",
        },
        {
            id: 5,
            title: "Cover Letter Generator",
            desc: "Create tailored cover letters in seconds. The AI analyzes job descriptions to highlight your relevant skills and experiences.",
            img: require("../img/cat-coverletter.png"),
            color: "bg-purple-200",
        },
        {
            id: 6,
            title: "Continuous Learning",
            desc: "AI learns from your interactions for better insights — customized skill refinement, suggested micro-tasks, and progress tracking dashboards.",
            img: require("../img/cat-learning.png"),
            color: "bg-yellow-200",
        },
    ];

    const faqs = [
        {
            id: 1,
            question: "What is the JobStep?",
            answer:
                "The JobStep is an advanced, AI-powered platform designed for early-career professionals. Its purpose is to provide a unified, intelligent ecosystem of tools that assist with every stage of the job search, screening, and professional development journey",
        },
        {
            id: 2,
            question: "Who is the target audience for this platform?",
            answer:
                "The platform is specifically engineered for early-career professionals who are either entering the workforce or navigating the initial stages of their careers. It aims to resolve the common challenges this demographic faces, such as crafting effective application materials and preparing for interviews",
        },
        {
            id: 3,
            question: "How does the AI Resume Builder and Scorer work?",
            answer:
                "The suite includes an AI Resume Builder that uses your work history, skills, and target job descriptions to generate a professionally formatted, ATS-friendly resume . It also features an AI Resume Scoring tool that evaluates your resume against industry benchmarks and specific job requirements, providing a numerical score and actionable feedback for improvement",
        },
        {
            id: 4,
            question: "What kind of interview practice is available?",
            answer:
                "The platform offers two types of AI-based interview simulations. Speech-to-Text AI Interview: You respond verbally to text questions, and the AI analyzes the transcribed text for content quality, accuracy, and structure8888. Complete AI Speech-to-Speech Interview: This offers a more realistic experience where an AI interviewer asks questions with a synthetic voice. It provides richer feedback on your content as well as your intonation, confidence, pacing, and use of filler words",
        },
        {
            id: 5,
            question: "How does the platform help with technical interviews?",
            answer:
                "For technical roles, the AI-Powered Coding Assessments module allows you to practice in a realistic environment. It can generate custom mock tests with unique problems tailored to specific roles and difficulty levels. You can also participate in timed challenges within a live coding interface that automatically grades your solution on correctness, time complexity, and space complexity",
        },
        {
            id: 6,
            question: "How does the Automated Job Matching feature work?",
            answer:
                "The platform uses a sophisticated Recommendation Engine that analyzes your profile, skills, and resume data. It then intelligently matches you with relevant opportunities from aggregated job databases and for each one, highlighting any skill or experience gaps you need to address",
        },
        {
            id: 7,
            question: "What makes JobStep different from other job tools?",
            answer:
                "Its key differentiator is its seamless, all-in-one approach . The tools are deeply interconnected to create a continuous feedback loop22. For instance, skill gaps identified from job matching can directly inform resume optimization and guide your career development path, creating a single, intelligent, and empowering journey",
        },

    ];

    return (
        <div className="bg-[#06010a] text-white font-poppins overflow-x-hidden">
            {/* Hero Section */}

            <section className="flex flex-col justify-center items-center text-center min-h-screen px-4 bg-gradient-to-b from-[#06010a] to-[#260345]">
                <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white">
                    Let AI refine your{''}
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600 font-extrabold ">
                        <Typewriter
                            words={['{Career Path}', '{Resume}']}
                            loop={true}
                            typeSpeed={85}
                            deleteSpeed={70}
                            cursor={'_'}
                            cursorSpeed={1000}
                        />
                    </span>
                </h1>

                <p className="text-lg text-gray-300 mb-8 max-w-2xl">
                    Combine human insight with AI precision to craft your career story, practice with
                    confidence, and achieve your professional goals.
                </p>


                <img
                    src={require("../img/mascot.png")}
                    alt="Mascot"
                    className="w-52 mb-5 animate-bounce" style={{ animationDuration: '2.5s' }}
                />
                <button
                    onClick={() => navigate("/auth")}
                    className="bg-[#ffc403] text-black font-bold px-6 py-3 rounded-xl hover:bg-[#e6b800] transition"
                >
                    Get Started
                </button>
            </section>


            {/* Features Section */}
            <section className="bg-white text-black py-20 px-8" id="features">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                    Our Core Features
                </h2>
                <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12">
                    Built for modern jobseekers and teams. Each feature is backed by explainable AI and
                    delivers tangible career outcomes.
                </p>
                <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {features.map((feature) => (
                        <div
                            key={feature.id}
                            className={`p-6 rounded-2xl shadow-md text-center cursor-pointer transition transform hover:scale-105 ${feature.color}`}
                            onClick={() =>
                                setOpenFeature(openFeature === feature.id ? null : feature.id)
                            }
                        >
                            <img
                                src={feature.img}
                                alt={feature.title}
                                className="w-24 mx-auto mb-4"
                            />
                            <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                            {openFeature === feature.id && (
                                <p className="text-sm text-gray-700 mt-2">{feature.desc}</p>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="bg-gradient-to-b from-[#06010a] to-[#260345] text-center py-20 px-8">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Trusted by Professionals Worldwide
                </h2>
                <p className="text-gray-400 max-w-3xl mx-auto mb-12">
                    Companies and individuals rely on JobStep to scale hiring, mentoring, and
                    upskilling. Here are a few of our happy users.
                </p>
                <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    {[
                        {
                            name: "Cian Murphy",
                            review: "A Game-Changer for My First Job Search\nAs a final-year student, I was completely overwhelmed by the job application process. The AI Resume Builder helped me create a professional, ATS-friendly resume from scratch. The interview simulation was the most helpful part; getting feedback not just on my answers but on my pacing and use of filler words gave me a huge confidence boost.",
                            img: require("../img/user1.jpg"),
                            role: "Computer Science Student"
                        },
                        {
                            name: "Nolan Walsh",
                            review: "Finally, a Smarter Way to Job Hunt. Before using JobStep, my job search was a numbers game sending out dozens of applications and hearing nothing back. The AI Resume Scoring was an eye-opener, it gave me a numerical score and specific feedback that helped me get past the initial ATS screenings. The best feature is the Automated Job Matching. It provides a personalized list of jobs with a fit score and even highlights my specific skill gaps for each role. ",
                            img: require("../img/user2.jpg"),
                            role: "Early-Career Job Hunter"
                        },
                        {
                            name: "Priya Sharma",
                            review: "More Than a Job Finder—It's a Career Planner\nI wasn't just looking for a new job; I wanted to be strategic about my next career move. The Personalized Career Path Guidance module is unlike anything I've seen. The AI analyzed my profile against my career goals to identify the most high-impact skills I needed to develop. It then generated a personalized, multi-step roadmap based on real market trends and anonymized peer trajectories. This platform has given me a clear vision and a structured plan for my long-term professional growth.",
                            img: require("../img/user3.jpg"),
                            role: "Young Professional"
                        },
                        {
                            name: "Kelly Wakasa",
                            review: "The Best Technical Interview Prep I've Found\nTechnical interviews were my biggest weakness. The AI-Powered Coding Assessments on this platform are top-notch. I was able to generate custom mock tests for a Software Engineer role at a 'Hard' difficulty level. The timed, live coding environment is an exact simulation of a real interview. What truly sets it apart is the Performance Analytics—it didn't just tell me if my code was correct, it gave me feedback on my problem-solving approach and pointed out how to improve my code's time and space complexity.",
                            img: require("../img/user4.jpg"),
                            role: "Aspiring Software Engineer"
                        },
                        {
                            name: "Emily Chen",
                            review: "Made My Career Change Possible\nSwitching industries felt daunting, especially when it came to re-writing my resume and cover letter. The AI Cover Letter Generator was a lifesaver; it helped me create a persuasive letter that connected my past experience to new job requirements. The Job-Fit Scoring was also incredibly valuable, as it showed me exactly which skills to emphasize. This tool gave me the confidence that my application materials were professional and perfectly tailored for the new field I was entering.",
                            img: require("../img/user5.jpg"),
                            role: "Frontend Developer"
                        },
                        {
                            name: "Ashley Alexander",
                            review: "A Game Changer for Job Seekers\nAs someone new to the job market, I faced numerous challenges in presenting my skills effectively. The AI Resume Builder was a game changer; it guided me through the process of creating a standout resume that highlighted my strengths. The real-time feedback and optimization tips were invaluable. I also appreciated the interview preparation resources, which helped me build confidence and improve my performance. I can't recommend this platform enough!",
                            img: require("../img/user6.jpg"),
                            role: "Recent Graduate"
                        },
                    ].map((user, i) => (
                        <div
                            key={i}
                            className="bg-white/10 backdrop-blur-md rounded-xl p-6 text-left"
                        >
                            <div className="flex items-center gap-4 mb-3">
                                <img
                                    src={user.img}
                                    alt={user.name}
                                    className="w-12 h-12 rounded-full border-2 border-purple-400"
                                />
                                <div>
                                    <h4 className="font-semibold text-white">{user.name}</h4>
                                    <p className="text-sm text-gray-400">
                                        {(() => {
                                            switch (user.name) {
                                                case "Cian Murphy": return "300+ resumes optimized";
                                                case "Nolan Walsh": return "800+ interviews aced";
                                                case "Priya Sharma": return "500+ jobs matched";
                                                case "Kelly Wakasa": return "200+ job oppurtunities explored";
                                                case "Emily Chen": return "600+ learning milestones achieved";
                                                case "Ashley Alexander": return "500 career goals reached";
                                                default: return "Happy user";
                                            }
                                        })()}
                                    </p>
                                </div>
                            </div>
                            <p className="text-gray-300 text-sm">
                                “{user.review}”
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ Section */}
            <section className="bg-white text-black py-20 px-8" id="faq">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                    Frequently Asked Questions
                </h2>
                <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
                    <div className="md:col-span-2 space-y-4">
                        {faqs.map((faq) => (
                            <div
                                key={faq.id}
                                className="border rounded-xl p-4 shadow-sm cursor-pointer"
                                onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
                            >
                                <div className="flex justify-between items-center">
                                    <h3 className="font-semibold">{faq.question}</h3>
                                    <ChevronDown
                                        className={`w-5 h-5 transition-transform ${openFAQ === faq.id ? "rotate-180" : ""
                                            }`}
                                    />
                                </div>
                                {openFAQ === faq.id && (
                                    <p className="mt-3 text-gray-600 text-sm">{faq.answer}</p>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="bg-gray-50 border rounded-xl p-8 flex flex-col justify-between">
                        <div>
                            <h3 className="font-semibold text-lg mb-2">Do you have more questions?</h3>
                            <p className="text-gray-600 text-sm mb-6">
                                Reach out to us directly — we’ll help you with personalized solutions for
                                your career growth and development.
                            </p>
                        </div>
                        <a href="mailto:info@novistep.com" className="bg-yellow-400 text-black font-semibold px-6 py-2 rounded-lg hover:bg-yellow-500 transition">
                            Shoot a Direct Mail
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            {/* <footer className="bg-gradient-to-b from-[#260345] to-[#06010a] py-6 text-center text-gray-400 text-sm">
                © 2025 RAKFORT. All rights reserved.
            </footer> */}
        </div >
    );
};

export default Desktop20;
